import { useState } from 'react';

import { useNavigateTo, useTreadwellPreferences } from '../hooks';

import { getShopTirePath } from '../../utils/fitment/fitment';

import { Fitment } from '../../types/fitment';
import { SEARCH_TYPES, SORT_PARAMS } from '../../constants/search-types';

interface UseProductSearchNavigation {
  fitment: Fitment;
  isModal?: boolean;
  isPackageFlow?: boolean;
}

export default function useProductSearchNavigation(
  props: UseProductSearchNavigation
) {
  const { fitment, isModal, isPackageFlow } = props;
  const { disableTreadwellFlow, hasTreadwellPreferences } =
    useTreadwellPreferences();
  const navigateTo = useNavigateTo();
  const [isTireCategoriesDisplayed, setIsTireCategoriesDisplayed] =
    useState(false);
  const [selectedSearchType, setSelectedSearchType] = useState('');

  function tireProductTypeStrategy() {
    if (isModal) {
      disableTreadwellFlow();
      navigateTo('#/fitment/shop-tires');
    } else {
      setIsTireCategoriesDisplayed(true);
    }
  }

  function accessoriesProductTypeStrategy() {
    disableTreadwellFlow();
    setSelectedSearchType(SEARCH_TYPES.ACCESSORIES);
  }

  const searchTypeStrategies = {
    // searchType|isOE|isStaggered

    // TIRES
    // staggered OE
    [`${SEARCH_TYPES.TIRES}|true|true`]: tireProductTypeStrategy,

    // staggered optional
    [`${SEARCH_TYPES.TIRES}|false|true`]: tireProductTypeStrategy,

    // non-staggered OE
    [`${SEARCH_TYPES.TIRES}|true|false`]: tireProductTypeStrategy,
    // non-staggered optional
    [`${SEARCH_TYPES.TIRES}|false|false`]: tireProductTypeStrategy,

    // PACKAGES
    [`${SEARCH_TYPES.PACKAGE}|true|false`]: () => {
      setSelectedSearchType(SEARCH_TYPES.PACKAGE);
    },
    [`${SEARCH_TYPES.PACKAGE}|false|false`]: () => {
      setSelectedSearchType(SEARCH_TYPES.PACKAGE);
      disableTreadwellFlow();
    },
    [`${SEARCH_TYPES.PACKAGE}|false|true`]: () => {
      disableTreadwellFlow();
      navigateTo('#/package/build');
    },
    [`${SEARCH_TYPES.PACKAGE}|true|true`]: () => {
      disableTreadwellFlow();
      navigateTo('#/package/build');
    },

    // WHEELS
    // staggered OE
    [`${SEARCH_TYPES.WHEELS}|true|true`]: () => {
      disableTreadwellFlow();
      navigateTo('/fitmentresult/wheels/sets');
    },
    // staggered optional
    [`${SEARCH_TYPES.WHEELS}|false|true`]: () => {
      disableTreadwellFlow();
      navigateTo(
        `/fitmentresult/wheels/optionalPlusSet/${fitment.frontWheelDiameter}/rear/${fitment.rearWheelDiameter}`
      );
    },
    // non-staggered OE
    [`${SEARCH_TYPES.WHEELS}|true|false`]: () => {
      disableTreadwellFlow();
      navigateTo(`/fitmentresult/wheels`);
    },
    // non-staggered optional
    [`${SEARCH_TYPES.WHEELS}|false|false`]: () => {
      disableTreadwellFlow();
      navigateTo(
        `/fitmentresult/wheels/optionalPlusSize/${fitment.frontWheelDiameter}`
      );
    },

    // ACCESSORIES
    // staggered OE
    [`${SEARCH_TYPES.ACCESSORIES}|true|true`]: accessoriesProductTypeStrategy,
    // staggered optional
    [`${SEARCH_TYPES.ACCESSORIES}|false|true`]: accessoriesProductTypeStrategy,
    // non-staggered OE
    [`${SEARCH_TYPES.ACCESSORIES}|true|false`]: accessoriesProductTypeStrategy,
    // non-staggered optional
    [`${SEARCH_TYPES.ACCESSORIES}|false|false`]: accessoriesProductTypeStrategy
  };

  function onProductTypeCleared() {
    setIsTireCategoriesDisplayed(false);
    setSelectedSearchType('');
  }

  function onSearchTypeSelected(searchType) {
    const searchTypeStrategy =
      searchTypeStrategies[
        `${searchType.toLowerCase()}|${Boolean(fitment?.isOE)}|${Boolean(
          fitment?.isStaggered || fitment?.isDually
        )}`
      ];

    searchTypeStrategy();
  }

  function onShopAllAccessories() {
    navigateTo('/fitmentresult/accessories');
  }

  function onShopAllTires() {
    if (hasTreadwellPreferences(fitment)) {
      navigateTo(
        getShopTirePath(fitment, isPackageFlow) +
          '?q=%3Arecommended&sort=recommended'
      );
    } else {
      disableTreadwellFlow();
      return navigateTo(getShopTirePath(fitment, isPackageFlow));
    }
  }

  function getSelectedTireCategoryQuery(tireCategory) {
    switch (tireCategory) {
      case SEARCH_TYPES.INVENTORY_STATUS: {
        return `?q=%3A${SORT_PARAMS.BEST_SELLER_ASC}%3AinventoryStatus%3AIn%20Stock&sort=${SORT_PARAMS.BEST_SELLER_ASC}`;
      }
      case SEARCH_TYPES.STORE_IN_STOCK: {
        return `?q=%3A${SORT_PARAMS.BEST_SELLER_ASC}%3AstoreInStock%3AIn%20Stock&sort=${SORT_PARAMS.BEST_SELLER_ASC}`;
      }
      case SEARCH_TYPES.PROMOTION: {
        return hasTreadwellPreferences(fitment)
          ? `?q=%3A${SORT_PARAMS.PRICE_ASC}%3AstorePromotion-boolean%3AOn%20Promotion%3Arebates-boolean%3ARebates%3Aall-tire-boolean%3AShow%20All%20Tires&sort=${SORT_PARAMS.PRICE_ASC}`
          : `?q=%3A${SORT_PARAMS.ON_PROMOTION_ASC}%3AinstantSavings-boolean%3AInstant%20Savings%3Arebates-boolean%3ARebates%3AspecialOffers-boolean%3ASpecial%20Offers&sort=${SORT_PARAMS.PRICE_ASC}`;
      }
      case SEARCH_TYPES.BEST: {
        return hasTreadwellPreferences(fitment)
          ? `?q=%3A${SORT_PARAMS.BEST_SELLER_ASC}%3Aall-tire-boolean%3AShow%20All%20Tires&sort=${SORT_PARAMS.BEST_SELLER_ASC}`
          : `?sort=${SORT_PARAMS.BEST_SELLER_ASC}`;
      }
      case SEARCH_TYPES.HIGHEST: {
        return hasTreadwellPreferences(fitment)
          ? `?q=%3A${SORT_PARAMS.RATING_DESC}%3Aall-tire-boolean%3AShow%20All%20Tires&sort=${SORT_PARAMS.RATING_DESC}`
          : `?q=%3A${SORT_PARAMS.RATING_DESC}&sort=${SORT_PARAMS.RATING_DESC}`;
      }
      case SEARCH_TYPES.OE: {
        return hasTreadwellPreferences(fitment)
          ? `?q=%3A${SORT_PARAMS.BEST_SELLER_ASC}%3Aoe-boolean%3AOriginal%20Equipment%3Aall-tire-boolean%3AShow%20All%20Tires&sort=${SORT_PARAMS.BEST_SELLER_ASC}`
          : `?q=:${SORT_PARAMS.BEST_SELLER_ASC}:oe-boolean:Original%20Equipment`;
      }
    }
  }

  function onTireCategorySelected(tireCategory) {
    const path = getShopTirePath(fitment, isPackageFlow);
    if (hasTreadwellPreferences(fitment) === false) disableTreadwellFlow();

    return navigateTo(path + getSelectedTireCategoryQuery(tireCategory));
  }

  const showBackButton =
    selectedSearchType === SEARCH_TYPES.ACCESSORIES ||
    selectedSearchType === SEARCH_TYPES.PACKAGE;

  return {
    isTireCategoriesDisplayed,
    onProductTypeCleared,
    onSearchTypeSelected,
    onShopAllAccessories,
    onShopAllTires,
    onTireCategorySelected,
    selectedSearchType,
    showBackButton
  };
}
